import React, { useState } from 'react';
import {
  Badge, Button, Card, Col, Image, Modal, Progress, Row, Segmented, Space, Steps, Tag, Typography, Input, Alert, Divider, Statistic,
} from 'antd';
import {
  CrownOutlined, RocketOutlined, StarOutlined, CheckCircleOutlined, QrcodeOutlined,
} from '@ant-design/icons';
import { formatCurrency } from 'utils/number';

const { Text, Title, Paragraph } = Typography;

const TIER_ICONS = {
  mien_phi: <StarOutlined />,
  mini: <RocketOutlined />,
  pro: <CrownOutlined />,
  vip: <CrownOutlined style={{ color: '#faad14' }} />,
};

const TIER_COLORS = {
  mien_phi: 'default',
  mini: 'blue',
  pro: 'purple',
  vip: 'gold',
};

const StorageLabel = (bytes) => {
  if (!bytes) return '0 MB';
  const gb = bytes / (1024 ** 3);
  return gb >= 1 ? `${gb.toFixed(2)} GB` : `${(bytes / (1024 ** 2)).toFixed(1)} MB`;
};

const Desktop = ({ viewModel }) => {
  const {
    loading, current, packages, currentTierOrder, TIER_ORDER,
    upgradeStep, selectedPackage, selectedDuration, upgradeRequest, paymentNote, submitting, pricing,
    onClickUpgrade, onCancelUpgrade, onConfirmSelect, onConfirmPayment,
    setSelectedDuration, setPaymentNote, getUsagePercent, getDaysLeft,
  } = viewModel;

  const [qrUrl, setQrUrl] = useState(null);

  const pkg = current?.package;
  const usage = current?.usage;
  const sub = current?.subscription;
  const daysLeft = getDaysLeft();

  const renderCurrentCard = () => (
    <Card
      title={
        <Space>
          {TIER_ICONS[current?.tier] || <StarOutlined />}
          <span>Gói hiện tại</span>
        </Space>
      }
      extra={
        pkg && (
          <Tag color={TIER_COLORS[current?.tier] || 'default'} style={{ fontSize: 14 }}>
            {pkg.name}
          </Tag>
        )
      }
    >
      {daysLeft !== null && (
        <Alert
          type={daysLeft <= 7 ? 'warning' : 'info'}
          message={
            daysLeft > 0
              ? `Còn ${daysLeft} ngày sử dụng`
              : 'Gói cước đã hết hạn, đã chuyển về Miễn Phí'
          }
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      {pkg && (
        <Row gutter={[24, 16]}>
          <Col span={8}>
            <div style={{ marginBottom: 6 }}>
              <Text type='secondary'>Sản phẩm</Text>
            </div>
            <Progress
              percent={getUsagePercent(usage?.product_count, pkg.limits.product_count)}
              format={() => `${usage?.product_count || 0} / ${pkg.limits.product_count}`}
            />
          </Col>
          <Col span={8}>
            <div style={{ marginBottom: 6 }}>
              <Text type='secondary'>Đơn hàng tháng này</Text>
            </div>
            <Progress
              percent={getUsagePercent(usage?.order_count_month, pkg.limits.order_count_month)}
              format={() => `${usage?.order_count_month || 0} / ${pkg.limits.order_count_month}`}
              strokeColor={getUsagePercent(usage?.order_count_month, pkg.limits.order_count_month) >= 95 ? '#ff4d4f' : undefined}
            />
          </Col>
          <Col span={8}>
            <div style={{ marginBottom: 6 }}>
              <Text type='secondary'>Cloud</Text>
            </div>
            <Progress
              percent={getUsagePercent(usage?.storage_used_bytes, pkg.limits.storage_gb * 1024 ** 3)}
              format={() => `${StorageLabel(usage?.storage_used_bytes)} / ${pkg.limits.storage_gb} GB`}
            />
          </Col>
        </Row>
      )}
    </Card>
  );

  const renderPackageCard = (p) => {
    const tierOrder = TIER_ORDER[p.key] ?? 0;
    const isCurrent = p.key === current?.tier;
    const isDowngrade = tierOrder < currentTierOrder;

    return (
      <Col span={6} key={p.key}>
        <Card
          style={{
            height: '100%',
            border: isCurrent ? '2px solid #1677ff' : undefined,
          }}
          bodyStyle={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        >
          <Space direction='vertical' size={8} style={{ width: '100%', flex: 1 }}>
            <Space>
              {TIER_ICONS[p.key]}
              <Text strong style={{ fontSize: 16 }}>{p.name}</Text>
              {isCurrent && <Tag color='blue'>Đang dùng</Tag>}
            </Space>

            <Title level={4} style={{ margin: 0 }}>
              {p.is_free ? 'Miễn phí' : `${formatCurrency(p.price_monthly)}đ/tháng`}
            </Title>

            <Divider style={{ margin: '8px 0' }} />

            <Space direction='vertical' size={4} style={{ width: '100%' }}>
              <Text><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />{p.limits.product_count} sản phẩm</Text>
              <Text><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />{p.limits.order_count_month} đơn/tháng</Text>
              <Text><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />{p.limits.storage_gb} GB cloud</Text>
            </Space>
          </Space>

          {!isCurrent && !p.is_free && (
            <Button
              type={isDowngrade ? 'default' : 'primary'}
              style={{ marginTop: 16 }}
              onClick={() => onClickUpgrade(p)}
              block
            >
              {isDowngrade ? 'Hạ xuống' : 'Nâng cấp'}
            </Button>
          )}
        </Card>
      </Col>
    );
  };

  const renderSelectStep = () => (
    <Space direction='vertical' size={16} style={{ width: '100%' }}>
      <div>
        <Text strong>Chọn kỳ hạn</Text>
        <div style={{ marginTop: 8 }}>
          <Segmented
            value={selectedDuration}
            onChange={setSelectedDuration}
            options={[
              { label: '1 tháng', value: 1 },
              { label: '3 tháng', value: 3 },
              { label: '6 tháng (+1 tặng)', value: 6 },
            ]}
          />
        </div>
      </div>

      {pricing && (
        <Card size='small' style={{ background: '#f0f5ff' }}>
          <Row gutter={16}>
            <Col span={12}>
              <Statistic title='Gói' value={selectedPackage?.name} />
            </Col>
            <Col span={12}>
              <Statistic title='Tổng thanh toán' value={`${formatCurrency(pricing.amount)}đ`} />
            </Col>
            <Col span={12}>
              <Statistic title='Thời gian' value={`${selectedDuration} tháng`} />
            </Col>
            {pricing.bonus_months > 0 && (
              <Col span={12}>
                <Statistic title='Tặng thêm' value={`+${pricing.bonus_months} tháng`} valueStyle={{ color: '#52c41a' }} />
              </Col>
            )}
          </Row>
        </Card>
      )}
    </Space>
  );

  const renderPaymentStep = () => {
    const info = upgradeRequest?.payment_info;
    const req = upgradeRequest?.request;
    const transferContent = `SIPRO ${(req?.id || '').slice(0, 8).toUpperCase()}`;
    const amount = req?.amount || upgradeRequest?.pricing?.amount || 0;
    const qrUrl = info?.bank_bin
      ? `https://img.vietqr.io/image/${info.bank_bin}-${info.account_number}-compact2.png?amount=${amount}&addInfo=${encodeURIComponent(transferContent)}&accountName=${encodeURIComponent(info.account_name || '')}`
      : null;
    return (
      <Space direction='vertical' size={16} style={{ width: '100%' }}>
        <Alert
          type='info'
          showIcon
          message='Vui lòng chuyển khoản và bấm Xác nhận đã thanh toán sau khi hoàn tất'
        />

        {info && (
          <Card
            size='small'
            title='Thông tin chuyển khoản'
            extra={
              qrUrl && (
                <Button
                  size='small'
                  icon={<QrcodeOutlined />}
                  onClick={() => setQrUrl(qrUrl)}
                >
                  Xem QR
                </Button>
              )
            }
          >
            <Space direction='vertical' size={4}>
              {info.bank_name && <Text><Text strong>Ngân hàng: </Text>{info.bank_name}</Text>}
              {info.account_number && <Text><Text strong>Số tài khoản: </Text><Text copyable>{info.account_number}</Text></Text>}
              {info.account_name && <Text><Text strong>Chủ tài khoản: </Text>{info.account_name}</Text>}
              <Text>
                <Text strong>Nội dung CK: </Text>
                <Text copyable>{transferContent}</Text>
              </Text>
              <Text><Text strong>Số tiền: </Text><Text style={{ color: '#1677ff', fontWeight: 700 }}>{formatCurrency(amount)}đ</Text></Text>
            </Space>
          </Card>
        )}

        <div>
          <Text strong>Ghi chú cho lần thanh toán này (tùy chọn)</Text>
          <Input.TextArea
            style={{ marginTop: 8 }}
            rows={2}
            placeholder='VD: Đã chuyển khoản lúc 10:30...'
            value={paymentNote}
            onChange={(e) => setPaymentNote(e.target.value)}
          />
        </div>
      </Space>
    );
  };

  const renderDoneStep = () => (
    <Space direction='vertical' align='center' style={{ width: '100%', padding: '24px 0' }}>
      <CheckCircleOutlined style={{ fontSize: 56, color: '#52c41a' }} />
      <Title level={4}>Đã ghi nhận xác nhận thanh toán</Title>
      <Paragraph type='secondary' style={{ textAlign: 'center' }}>
        Đội ngũ Sipro sẽ kiểm tra và kích hoạt gói của bạn trong thời gian sớm nhất.
        Bạn sẽ nhận được thông báo khi gói được kích hoạt.
      </Paragraph>
    </Space>
  );

  const modalStepLabels = ['Chọn kỳ hạn', 'Thanh toán', 'Hoàn tất'];
  const stepIndex = upgradeStep === 'select' ? 0 : upgradeStep === 'payment' ? 1 : 2;

  return (
    <Space direction='vertical' size={24} style={{ width: '100%' }}>
      <Title level={4} style={{ marginBottom: 0 }}>Gói cước</Title>

      {renderCurrentCard()}

      <div>
        <Title level={5} style={{ marginBottom: 12 }}>Tất cả gói cước</Title>
        <Row gutter={[16, 16]}>
          {packages.map(renderPackageCard)}
        </Row>
      </div>

      <Modal
        open={!!upgradeStep}
        title={selectedPackage ? `Nâng cấp lên ${selectedPackage.name}` : 'Gói cước'}
        onCancel={onCancelUpgrade}
        footer={
          upgradeStep === 'done' ? (
            <Button type='primary' onClick={onCancelUpgrade}>Đóng</Button>
          ) : upgradeStep === 'select' ? (
            <Space>
              <Button onClick={onCancelUpgrade}>Hủy</Button>
              <Button type='primary' onClick={onConfirmSelect} loading={submitting}>
                Tiếp tục
              </Button>
            </Space>
          ) : (
            <Space>
              <Button onClick={onCancelUpgrade}>Hủy</Button>
              <Button type='primary' onClick={onConfirmPayment} loading={submitting}>
                Xác nhận đã thanh toán
              </Button>
            </Space>
          )
        }
        width={560}
        destroyOnClose
      >
        <Steps
          size='small'
          current={stepIndex}
          items={modalStepLabels.map((label) => ({ title: label }))}
          style={{ marginBottom: 24 }}
        />
        {upgradeStep === 'select' && renderSelectStep()}
        {upgradeStep === 'payment' && renderPaymentStep()}
        {upgradeStep === 'done' && renderDoneStep()}
      </Modal>

      <Modal
        open={!!qrUrl}
        title='QR chuyển khoản'
        footer={<Button type='primary' onClick={() => setQrUrl(null)}>Đóng</Button>}
        onCancel={() => setQrUrl(null)}
        centered
        width={320}
      >
        {qrUrl && (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <img src={qrUrl} alt='VietQR' style={{ width: 260, height: 260, objectFit: 'contain' }} />
          </div>
        )}
      </Modal>
    </Space>
  );
};

export default Desktop;
